import { useMemo } from 'react';

import * as z from 'zod/v4-mini';

import { useFeatureFlag } from '@order/LaunchDarkly';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns the "too-far" threshold for the target location.
 *
 * - Delivery uses a fixed one-mile threshold, while pickup uses the matching configured market area.
 * - The pickup LaunchDarkly value is parsed with Zod Mini before any market-area data is used.
 * - Each market-area key must contain a positive `distanceInMiles` and a four-item `mapBounds`
 *   tuple ordered as `[topLeftLatitude, topLeftLongitude, bottomRightLatitude, bottomRightLongitude]`.
 * - Invalid configuration or pickup coordinates outside the configured bounds return 0 without throwing.
 * - Outpost returns 0 and does not use the "too-far" check.
 * - Returns 0 when the "too-far" check should be disabled.
 */
export const useTooFarLocationThresholdInMiles = (params: Params) => {
  const { channel, targetPosition } = params;

  const pickupMarketAreaConfig = useFeatureFlag('CELS-5744-order-pickup-market-area-too-far-config'); // prettier-ignore

  const targetLatitude = targetPosition?.latitude;
  const targetLongitude = targetPosition?.longitude;
  const hasTargetPosition = targetLatitude !== undefined && targetLongitude !== undefined;

  // ─────────────────────────────────────────────────────────────────────

  return useMemo(() => {
    const canEvaluateThreshold = hasTargetPosition && channel !== 'outpost';

    if (!canEvaluateThreshold) {
      return 0;
    }

    return getTooFarLocationThresholdInMiles({
      channel,
      pickupMarketAreaConfig,
      targetPosition: { latitude: targetLatitude, longitude: targetLongitude },
    });
  }, [channel, hasTargetPosition, pickupMarketAreaConfig, targetLatitude, targetLongitude]);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getTooFarLocationThresholdInMiles(params: GetThresholdParams) {
  const { channel, pickupMarketAreaConfig, targetPosition } = params;

  if (channel === 'delivery') {
    return DELIVERY_TOO_FAR_THRESHOLD_IN_MILES;
  }

  const canUsePickupMarketAreaConfig = channel === 'pickup';

  if (!canUsePickupMarketAreaConfig) {
    return 0;
  }

  const marketAreaConfigResult = pickupMarketAreaConfigSchema.safeParse(pickupMarketAreaConfig);

  if (!marketAreaConfigResult.success) {
    return 0;
  }

  const marketAreaConfigValues = Object.values(marketAreaConfigResult.data);
  const matchingMarketArea = marketAreaConfigValues.find(({ mapBounds }) =>
    checkIfPositionWithinMapBounds(targetPosition, mapBounds),
  );

  return matchingMarketArea?.distanceInMiles ?? 0;
}

function checkIfPositionWithinMapBounds(position: PositionCoordinates, mapBounds: MapBounds) {
  const { latitude, longitude } = position;

  const [topLeftLatitude, topLeftLongitude, bottomRightLatitude, bottomRightLongitude] = mapBounds;

  const isWithinLatitudeBounds = latitude <= topLeftLatitude && latitude >= bottomRightLatitude;
  const isWithinLongitudeBounds = longitude >= topLeftLongitude && longitude <= bottomRightLongitude; // prettier-ignore

  return isWithinLatitudeBounds && isWithinLongitudeBounds;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const DELIVERY_TOO_FAR_THRESHOLD_IN_MILES = 1;

// ─── Schema ──────────────────────────────────────────────────────────────────

const latitudeSchema = z.number().check(z.gte(-90), z.lte(90));
const longitudeSchema = z.number().check(z.gte(-180), z.lte(180));

const mapBoundsSchema = z
  .tuple([latitudeSchema, longitudeSchema, latitudeSchema, longitudeSchema])
  .check(
    z.refine(
      ([topLeftLatitude, topLeftLongitude, bottomRightLatitude, bottomRightLongitude]) =>
        topLeftLatitude >= bottomRightLatitude && topLeftLongitude <= bottomRightLongitude,
    ),
  );

const marketAreaSchema = z.object({
  mapBounds: mapBoundsSchema,
  distanceInMiles: z.number().check(z.positive()),
});

const pickupMarketAreaConfigSchema = z.record(z.string().check(z.minLength(1)), marketAreaSchema);

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  channel?: OrderChannel;
  targetPosition?: PositionCoordinates;
};

type GetThresholdParams = Omit<Params, 'targetPosition'> & {
  pickupMarketAreaConfig: unknown;
  targetPosition: PositionCoordinates;
};

type OrderChannel = 'delivery' | 'outpost' | 'pickup';
type MapBounds = z.infer<typeof mapBoundsSchema>;

type PositionCoordinates = {
  latitude: number;
  longitude: number;
};
